// petconnect-v2/frontend/src/components/features/Feed/Post.js
// UPDATED:
//   - Clicking post image, content, or pet info navigates to /post/:id
//   - "View full post" appears in hover and in ⋮ menu
//   - All existing like/comment/delete/resolved logic preserved

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Heart, MessageCircle, Trash2, Send, MapPin, MoreVertical, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAPI } from '../../../hooks/useAPI';
import PublicUserProfile from '../Profile/PublicUserProfile';

const Post = ({ post, currentUser, onUpdate, onDelete }) => {
  const { apiCall }  = useAPI();
  const navigate     = useNavigate();
  const [showComments, setShowComments]     = useState(false);
  const [showMenu, setShowMenu]             = useState(false);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [comments, setComments]             = useState([]);
  const [newComment, setNewComment]         = useState('');
  const [liked, setLiked]                   = useState(post.liked);
  const [likesCount, setLikesCount]         = useState(post.likes_count);
  const menuRef = useRef(null);

  const goToPost = () => navigate(`/post/${post.id}`);

  const loadComments = useCallback(async () => {
    try {
      const data = await apiCall(`/posts/${post.id}/comments`);
      setComments(data);
    } catch (err) {
      console.error('Error loading comments:', err);
    }
  }, [post.id, apiCall]);

  const handleLike = async (e) => {
    e.stopPropagation();
    try {
      if (liked) {
        await apiCall(`/posts/${post.id}/unlike`, { method: 'DELETE' });
        setLiked(false);
        setLikesCount(likesCount - 1);
      } else {
        await apiCall(`/posts/${post.id}/like`, { method: 'POST' });
        setLiked(true);
        setLikesCount(likesCount + 1);
      }
    } catch (err) {
      console.error('Error toggling like:', err);
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    try {
      await apiCall(`/posts/${post.id}/comments`, {
        method: 'POST',
        body: JSON.stringify({ content: newComment }),
      });
      setNewComment('');
      loadComments();
    } catch (err) {
      alert(err.message);
    }
  };

  const markResolved = async () => {
    try {
      const newStatus = post.status === 'resolved' ? 'active' : 'resolved';
      await apiCall(`/posts/${post.id}`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus }),
      });
      setShowMenu(false);
      onUpdate();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await onDelete(post.id);
        setShowMenu(false);
      } catch (err) {
        alert(err.message);
      }
    }
  };

  const handleReport = () => {
    alert('Post reported successfully. Thank you for helping keep our community safe.');
    setShowMenu(false);
  };

  const handleNotification = () => {
    alert('Notifications turned on for this post');
    setShowMenu(false);
  };

  const deleteComment = async (commentId) => {
    try {
      await apiCall(`/comments/${commentId}`, { method: 'DELETE' });
      loadComments();
    } catch (err) {
      alert(err.message);
    }
  };

  useEffect(() => {
    if (showComments) loadComments();
  }, [showComments, loadComments]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const categoryColors = {
    adoption:        'bg-pink-500',
    missing:         'bg-red-500',
    found:           'bg-green-500',
    success_stories: 'bg-yellow-500',
    general:         'bg-purple-500',
  };
  const bgColor = categoryColors[post.category] || 'bg-purple-500';

  return (
    <>
      <div className="bg-white rounded-xl shadow-lg p-3 md:p-4 lg:p-6 mb-4 hover:shadow-xl transition group">

        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2 md:gap-3 flex-1">
            <button
              onClick={(e) => { e.stopPropagation(); setShowUserProfile(true); }}
              className="flex-shrink-0 w-10 md:w-12 h-10 md:h-12 rounded-full overflow-hidden hover:opacity-80 transition"
            >
              {post.author.profile_picture ? (
                <img src={post.author.profile_picture} alt={post.author.username} className="w-full h-full object-cover" />
              ) : (
                <div className={`w-full h-full ${bgColor} flex items-center justify-center text-white font-bold text-lg`}>
                  {post.author.username[0].toUpperCase()}
                </div>
              )}
            </button>
            <div>
              <button
                onClick={(e) => { e.stopPropagation(); setShowUserProfile(true); }}
                className="font-bold text-gray-800 hover:underline text-left text-sm md:text-base"
              >
                {post.author.username}
              </button>
              <p className="text-xs md:text-sm text-gray-500">
                {new Date(post.created_at).toLocaleDateString()} •{' '}
                <span className="font-semibold text-gray-700">
                  {post.category.replace('_', ' ').toUpperCase()}
                </span>
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1">
            {/* View full post icon — visible on hover */}
            <button
              onClick={goToPost}
              title="View full post"
              className="p-2 text-gray-400 hover:text-purple-500 hover:bg-purple-50 rounded-full transition opacity-0 group-hover:opacity-100"
            >
              <ExternalLink size={16} />
            </button>

            {/* ⋮ menu */}
            <div className="relative" ref={menuRef}>
              <button
                onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
                className="p-2 hover:bg-gray-100 rounded-full transition"
              >
                <MoreVertical size={20} className="text-gray-600" />
              </button>

              {showMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-xl z-10">
                  <button
                    onClick={goToPost}
                    className="w-full text-left px-4 py-3 hover:bg-purple-50 text-purple-600 font-medium border-b border-gray-100 flex items-center gap-2 transition"
                  >
                    <ExternalLink size={16} />
                    View Full Post
                  </button>

                  {currentUser.id === post.user_id && (
                    <button
                      onClick={handleDelete}
                      className="w-full text-left px-4 py-3 hover:bg-red-50 text-red-600 font-medium border-b border-gray-100 flex items-center gap-2 transition"
                    >
                      <Trash2 size={16} />
                      Delete Post
                    </button>
                  )}

                  {post.category === 'missing' && currentUser.id === post.user_id && (
                    <button
                      onClick={markResolved}
                      className={`w-full text-left px-4 py-3 font-medium border-b border-gray-100 flex items-center gap-2 transition ${
                        post.status === 'resolved'
                          ? 'hover:bg-orange-50 text-orange-600'
                          : 'hover:bg-green-50 text-green-600'
                      }`}
                    >
                      {post.status === 'resolved' ? '↩️ Undo - Mark as Missing' : '✓ Mark as Reunited with Owner'}
                    </button>
                  )}

                  {currentUser.id !== post.user_id && (
                    <button
                      onClick={handleReport}
                      className="w-full text-left px-4 py-3 hover:bg-yellow-50 text-yellow-600 font-medium border-b border-gray-100 flex items-center gap-2 transition"
                    >
                      🚩 Report Post
                    </button>
                  )}

                  <button
                    onClick={handleNotification}
                    className="w-full text-left px-4 py-3 hover:bg-blue-50 text-blue-600 font-medium flex items-center gap-2 transition"
                  >
                    🔔 Turn on Notifications
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Status Badge */}
        {post.status === 'resolved' && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-2 rounded-lg mb-4 flex items-center gap-2">
            <span className="font-semibold">✓ Pet Reunited with Owner! 🎉</span>
          </div>
        )}

        {/* Pet Info — clickable */}
        {post.pet_name && (
          <button
            onClick={goToPost}
            className="w-full text-left bg-blue-50 border border-blue-200 rounded-lg p-2 md:p-3 mb-4 hover:bg-blue-100 transition"
          >
            <p className="font-bold text-blue-900 text-sm md:text-base">🐾 Pet: {post.pet_name}</p>
            {post.pet_type && (
              <p className="text-xs md:text-sm text-blue-700">
                Type: {post.pet_type}
                {post.breed && post.pet_type === 'dog' && ` • Breed: ${post.breed}`}
              </p>
            )}
            {post.sex && (
              <p className="text-xs md:text-sm text-blue-700">
                Sex: {post.sex.charAt(0).toUpperCase() + post.sex.slice(1)}
              </p>
            )}
            {post.missing_since && (
              <p className="text-xs md:text-sm text-blue-700">
                {post.category === 'missing' ? 'Missing Since' : 'Found On'}: {new Date(post.missing_since).toLocaleDateString()}
              </p>
            )}
            {post.contact_info && (
              <p className="text-xs md:text-sm text-blue-700">📞 Contact: {post.contact_info}</p>
            )}
          </button>
        )}

        {/* Post content — clickable */}
        <button onClick={goToPost} className="w-full text-left">
          <p className="text-gray-800 mb-4 whitespace-pre-wrap leading-relaxed text-sm md:text-base line-clamp-4 hover:text-gray-700 transition">
            {post.content}
          </p>
        </button>

        {/* Media — clickable */}
        {post.media_urls && post.media_urls.length > 0 && (
          <button onClick={goToPost} className="w-full mb-4 block">
            {post.media_urls.length === 1 ? (
              <div className="rounded-lg overflow-hidden">
                <img
                  src={post.media_urls[0]}
                  alt="Post media"
                  className="w-full max-h-72 object-cover hover:opacity-95 transition"
                />
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {post.media_urls.map((url, index) => (
                  <div key={index} className="rounded-lg overflow-hidden relative">
                    <img
                      src={url}
                      alt={`Media ${index + 1}`}
                      className="w-full h-24 md:h-32 object-cover hover:opacity-95 transition"
                    />
                    {index === 2 && post.media_urls.length > 3 && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white font-bold text-lg">
                        +{post.media_urls.length - 3}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </button>
        )}

        {/* Location */}
        {post.location_name && (
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-4 bg-gray-50 p-2 rounded">
            <MapPin size={16} />
            <span>{post.location_name}</span>
          </div>
        )}

        {/* Engagement */}
        <div className="flex flex-col md:flex-row items-center md:items-center gap-3 md:gap-6 mb-4 pt-4 border-t">
          <button
            onClick={handleLike}
            className={`w-full md:w-auto flex items-center gap-2 justify-center md:justify-start transition font-semibold ${
              liked ? 'text-red-500' : 'text-gray-600 hover:text-red-500'
            }`}
          >
            <Heart size={20} fill={liked ? 'currentColor' : 'none'} />
            <span>{likesCount}</span>
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); setShowComments(!showComments); }}
            className="w-full md:w-auto flex items-center gap-2 justify-center md:justify-start text-gray-600 hover:text-purple-500 transition font-semibold"
          >
            <MessageCircle size={20} />
            <span>{post.comments_count}</span>
          </button>

          {/* View full post link */}
          <button
            onClick={goToPost}
            className="w-full md:w-auto md:ml-auto flex items-center gap-1 text-xs text-gray-400 hover:text-purple-500 transition"
          >
            <ExternalLink size={13} />
            View full post
          </button>
        </div>

        {/* Inline comments */}
        {showComments && (
          <div className="border-t pt-4 space-y-4">
            <form onSubmit={handleComment} className="flex flex-col md:flex-row gap-2">
              <input
                type="text"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Write a comment..."
                className="flex-1 px-3 md:px-4 py-2 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                onClick={(e) => e.stopPropagation()}
              />
              <button type="submit" className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition">
                <Send size={18} />
              </button>
            </form>

            <div className="space-y-3">
              {comments.length === 0 ? (
                <p className="text-center text-gray-500 py-4 text-sm">No comments yet</p>
              ) : (
                comments.map((comment) => (
                  <div key={comment.id} className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 bg-purple-400 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                          {comment.author.username[0].toUpperCase()}
                        </div>
                        <div>
                          <p className="font-semibold text-sm">{comment.author.username}</p>
                          <p className="text-xs text-gray-500">{new Date(comment.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                      {currentUser.id === comment.user_id && (
                        <button
                          onClick={() => deleteComment(comment.id)}
                          className="text-red-500 hover:text-red-700 transition p-1"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                    <p className="text-gray-700 text-sm ml-10">{comment.content}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {showUserProfile && (
        <PublicUserProfile
          userId={post.author.id}
          currentUserId={currentUser.id}
          onClose={() => setShowUserProfile(false)}
        />
      )}
    </>
  );
};

export default Post;